import ChevronBottom from '@assets/icons/chevron-down.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { Animated, Easing, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { get12HourFormat } from '@/utils/date';

export interface BusinessHours {
  endDay: number;
  endHour: number;
  endMinute: number;
  startDay: number;
  startHour: number;
  startMinute: number;
}

interface Props {
  businessHours: BusinessHours[];
}

const DAYS_OF_WEEK = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const NUM_DAYS_OF_WEEK = 7;

export const RestaurantBusinessHours: React.FC<Props> = ({ businessHours }) => {
  const today = new Date();
  const currentDayOfWeek = today.getDay();
  const currentHour = today.getHours();
  const currentMinute = today.getMinutes();
  const [showOpenHours, setShowOpenHours] = useState(false);
  const animatedValueRef = React.useRef(new Animated.Value(0));
  const openHoursContentHeightRef = React.useRef(0);
  const [styles, theme] = useStyles();

  useEffect(() => {
    Animated.timing(animatedValueRef.current, {
      duration: 250,
      easing: Easing.out(Easing.exp),
      toValue: showOpenHours ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [showOpenHours]);

  const dayToBusinessHoursMap = useMemo(() => {
    return businessHours.reduce<{ [day: number]: BusinessHours[] }>(
      (result, entry) => {
        if (!result[entry.startDay]) {
          result[entry.startDay] = [];
        }
        result[entry.startDay].push(entry);
        return result;
      },
      [],
    );
  }, [businessHours]);

  // When a restaurant is open until the following day, the endDay is set to the next day.
  // This function handles the corner case where the current day is the next day but the active
  // open hour slot starts in the previous day and ends in the next day.
  // For example, it's 1am on Tuesday and the restaurant is open from 6pm on Monday to 2am on Tuesday.
  const yesterdayActiveHours = useMemo(() => {
    const yesterdayIndex =
      (currentDayOfWeek - 1 + NUM_DAYS_OF_WEEK) % NUM_DAYS_OF_WEEK;
    const yesterdayOpenHours = dayToBusinessHoursMap[yesterdayIndex];
    if (!yesterdayOpenHours) {
      return null;
    }

    const lastOpenHourOfYesterday =
      yesterdayOpenHours[yesterdayOpenHours.length - 1];
    if (
      (lastOpenHourOfYesterday.endDay === currentDayOfWeek &&
        lastOpenHourOfYesterday.endHour > currentHour) ||
      (lastOpenHourOfYesterday.endDay === currentDayOfWeek &&
        lastOpenHourOfYesterday.endHour === currentHour &&
        lastOpenHourOfYesterday.endMinute > currentMinute)
    ) {
      return lastOpenHourOfYesterday;
    }
  }, [currentDayOfWeek, currentHour, currentMinute, dayToBusinessHoursMap]);

  const nextOpenSlot = useMemo(() => {
    if (yesterdayActiveHours) {
      return yesterdayActiveHours;
    }

    var dayIndex = currentDayOfWeek;
    for (var i = 0; i < NUM_DAYS_OF_WEEK; i++) {
      const openHours = dayToBusinessHoursMap[dayIndex];
      if (openHours) {
        // Restaurant is operating in a later day, get the first open hour slot
        if (dayIndex !== currentDayOfWeek) {
          return openHours[0];
        }

        // Restaurant is operating today, find the active open hour slot
        for (var openHour of openHours) {
          // Add 24 hours to the end hour if the end day is the next day
          const endHour =
            (openHour.endDay - openHour.startDay + NUM_DAYS_OF_WEEK) %
              NUM_DAYS_OF_WEEK ===
            1
              ? 24 + openHour.endHour
              : openHour.endHour;

          if (
            (openHour.startHour < currentHour && endHour > currentHour) ||
            (openHour.startHour === currentHour &&
              openHour.startMinute <= currentMinute) ||
            (endHour === currentHour && openHour.endMinute >= currentMinute)
          ) {
            // Restaurant is currently open
            return openHour;
          } else if (
            openHour.startHour > currentHour ||
            (openHour.startHour === currentHour &&
              openHour.startMinute > currentMinute)
          ) {
            // Restaurant is currently closed but check if it will be open later today
            return openHour;
          }
        }
      }

      // No open hours found for today, check the next day
      dayIndex = (dayIndex + 1) % NUM_DAYS_OF_WEEK;
    }

    return null;
  }, [
    currentDayOfWeek,
    currentHour,
    currentMinute,
    dayToBusinessHoursMap,
    yesterdayActiveHours,
  ]);

  const renderOpenHours = useMemo(() => {
    // Start with Monday
    const days = [1, 2, 3, 4, 5, 6, 0];

    return days.reduce<JSX.Element[]>((result, day) => {
      let isBold = yesterdayActiveHours
        ? day === yesterdayActiveHours.startDay
        : day === currentDayOfWeek;
      const openHours = dayToBusinessHoursMap[day];
      const openHoursElement = (
        <View key={day} style={styles.contentBase}>
          <Text style={[styles.dayEntry, isBold ? styles.boldEntry : null]}>
            {i18n.t(DAYS_OF_WEEK[day])}
          </Text>
          {openHours ? (
            <View>
              {openHours.map((entry, index) => (
                <Text
                  key={index}
                  style={[styles.hourEntry, isBold ? styles.boldEntry : null]}
                >
                  {get12HourFormat(entry.startHour, entry.startMinute) +
                    ' - ' +
                    get12HourFormat(entry.endHour, entry.endMinute)}
                </Text>
              ))}
            </View>
          ) : (
            <Text style={[styles.hourEntry, isBold ? styles.boldEntry : null]}>
              {i18n.t('closed')}
            </Text>
          )}
        </View>
      );
      result.push(openHoursElement);
      return result;
    }, []);
  }, [currentDayOfWeek, dayToBusinessHoursMap, styles, yesterdayActiveHours]);

  const currentState = useMemo(() => {
    const textElements: JSX.Element[] = [];
    const closedText = (
      <Text key="closed" style={styles.closed}>
        {i18n.t('closed')}
      </Text>
    );
    const opensText = <Text key="opens">{' • ' + i18n.t('opens')}</Text>;
    const opensTomorrowText = (
      <Text key="tomorrow">
        {' • ' + i18n.t('opens') + ' ' + i18n.t('tomorrow')}
      </Text>
    );
    const openTodayText = (
      <Text key="open_today" style={styles.openToday}>
        {i18n.t('open') + ' ' + i18n.t('today')}
      </Text>
    );
    const openTimeText = (
      startHr: number,
      startMin: number,
      endHr?: number,
      endMin?: number,
    ) => {
      return (
        <Text key="open_time">
          {' ' +
            get12HourFormat(startHr, startMin) +
            (endHr !== undefined && endMin !== undefined
              ? ' - ' + get12HourFormat(endHr, endMin)
              : '')}
        </Text>
      );
    };

    if (!nextOpenSlot) {
      // Closed permanently
      textElements.push(closedText);
    } else if (nextOpenSlot.startDay === currentDayOfWeek) {
      // Closed but will be open later in the day
      if (
        currentHour < nextOpenSlot.startHour ||
        (currentHour === nextOpenSlot.startHour &&
          currentMinute < nextOpenSlot.startMinute)
      ) {
        textElements.push(closedText);
        textElements.push(opensText);
        textElements.push(
          openTimeText(nextOpenSlot.startHour, nextOpenSlot.startMinute),
        );
      } else {
        textElements.push(openTodayText);
        textElements.push(
          openTimeText(
            nextOpenSlot.startHour,
            nextOpenSlot.startMinute,
            nextOpenSlot.endHour,
            nextOpenSlot.endMinute,
          ),
        );
      }
    } else if (nextOpenSlot.endDay === currentDayOfWeek) {
      // Open now and open hours extend overnight
      if (
        nextOpenSlot.endHour > currentHour ||
        (nextOpenSlot.endHour === currentHour &&
          nextOpenSlot.endMinute > currentMinute)
      ) {
        textElements.push(openTodayText);
        textElements.push(
          openTimeText(
            nextOpenSlot.startHour,
            nextOpenSlot.startMinute,
            nextOpenSlot.endHour,
            nextOpenSlot.endMinute,
          ),
        );
      }
    } else if (
      nextOpenSlot.startDay ===
      (currentDayOfWeek + 1) % NUM_DAYS_OF_WEEK
    ) {
      // Closed but will be open tomorrow
      textElements.push(closedText);
      textElements.push(opensTomorrowText);
      textElements.push(
        openTimeText(nextOpenSlot.startHour, nextOpenSlot.startMinute),
      );
    } else if (nextOpenSlot.startDay) {
      // Closed but will be open later in the week
      textElements.push(closedText);
      textElements.push(opensText);
      textElements.push(
        <Text>{' ' + i18n.t(DAYS_OF_WEEK[nextOpenSlot.startDay])}</Text>,
      );
      textElements.push(
        openTimeText(nextOpenSlot.startHour, nextOpenSlot.startMinute),
      );
    }

    return textElements;
  }, [currentDayOfWeek, currentHour, currentMinute, nextOpenSlot, styles]);

  return (
    <View>
      <TouchableOpacity
        onPress={() => {
          setShowOpenHours((open) => !open);
        }}
        style={styles.touchable}
        testID="business-hours-dropdown-button"
      >
        <Text style={[TYPOGRAPHY.h4, styles.openHours]}>{currentState}</Text>
        <Animated.View
          style={[
            {
              transform: [
                {
                  rotate: animatedValueRef.current.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '-180deg'],
                  }),
                },
              ],
            },
          ]}
        >
          <ChevronBottom fill={theme.colors.primary} width={12} />
        </Animated.View>
      </TouchableOpacity>
      <Animated.View
        style={[
          styles.contentAnimatedView,
          {
            height: animatedValueRef.current.interpolate({
              inputRange: [0, 1],
              outputRange: [0, openHoursContentHeightRef.current],
            }),
          },
        ]}
      >
        <View
          onLayout={(e) => {
            openHoursContentHeightRef.current = e.nativeEvent.layout.height;
          }}
          style={styles.contentWrapper}
        >
          {renderOpenHours}
        </View>
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  boldEntry: {
    ...FONTS.primaryBold,
  },
  closed: {
    color: theme.colors.negative,
  },
  contentAnimatedView: {
    overflow: 'hidden',
  },
  contentBase: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  contentWrapper: {
    left: 0,
    maxWidth: 400,
    paddingTop: 8,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  dayEntry: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 14,
    lineHeight: 20,
  },
  hourEntry: {
    ...FONTS.primary,
    color: theme.colors.primary,
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'right',
  },
  openHours: {
    paddingRight: 8,
  },
  openToday: {
    color: theme.colors.darkGreen,
  },
  touchable: {
    alignItems: 'center',
    flexDirection: 'row',
  },
}));
